<template>
  <main id="contents" class="list-view-more">
    <div class="list-view">
      <div class="top-section">
        <div class="inner">
          <component
            :is="isApiTypeBand ? EditorPickHeader : EditorPickHeaderCatalog"
            v-if="isEditorPick"
            :title-list="titleList"
            :top-button-list="topButtonList"
          />
          <NoSubCategoryDropdown
            v-else
            :title="title"
            :filter-list="filterListRef"
            :filter-values="queryRef"
            @changeFilter="onChangeSideFilter"
          />
        </div>
      </div>

      <div v-if="hasCellList" class="contents-section inner">
        <div class="context-list">
          <div class="list-view-detail">
            <infinite-scroll-list @load-more-fn="loadMore">
              <component
                :is="catalogList"
                :key="`${originQuery.code}__list`"
                :context-list="contextList"
                :thumbnail-info="thumbnailInfo"
                logger="editor-pick"
              />
            </infinite-scroll-list>
          </div>
        </div>
      </div>

      <EmptyPage v-else-if="(!isLoading && !hasCellList) || isError" />
    </div>
  </main>
</template>
<script setup>
  import { computed, ref, watch } from 'vue'
  import { useRouter, useRoute } from 'vue-router/composables'
  import { useStore } from '@/common/utils/store'
  import { stringify } from 'query-string'
  import { PATH_NAME_LIST_VIEW_MORE } from '@/common/resources/router.const'
  import { queryStringToObject } from '@/common/utils/shared'
  import {
    getThumbnailInfo,
    titleTagNameChange,
    scrollTop,
  } from '@/common/utils/common'
  import { useCategorySub } from '@/common/use/useCategorySub'
  import { useSideFilter } from '@/common/use/useSideFilter'
  import { isEmptyObject } from '@/common/converter/utils/is-empty-object'
  import { getViewMoreHomeData } from '@/common/requests/data-fetch/get-view-more-home-data'
  import { getFetchUrl } from '@/common/utils/home/get-fetch-url'
  import { useRouterComposable } from '@/common/use/useRouterComposable'
  import { getTitle } from '@/common/utils/home/get-title'

  import EmptyPage from '@/pages/list/EmptyList.vue'
  import InfiniteScrollList from '@/pages/list/InfiniteScrollList.vue'
  import NoSubCategoryDropdown from '@/components/category/Sub/Header/NoSubCategoryDropdown.vue'
  import EditorPickHeaderCatalog from '@/pages/list/view-more/EditorPickHeaderCatalog.vue'
  import EditorPickHeader from '@/pages/list/EditorPickHeader'
  import ListCatalog from '@/pages/list/view-more/ListCatalog.vue'
  import PreviewListCatalog from '@/pages/list/view-more/PreviewListCatalog.vue'

  const { pushRouter } = useRouterComposable()
  const { replaceRouter, fetchData, setApiType, apiType } = useCategorySub()

  const { filterListRef, getAppendQuery, getSideFilterQuery } = useSideFilter()

  const router = useRouter()
  const route = useRoute()
  const store = useStore()

  // api call url, queries
  const apiUrl = ref('')
  const apiQuery = ref({})
  const fetchUrl = ref('')
  const originUrl = ref('')
  const appendQuery = ref({})
  const originQuery = ref({})
  const queryRef = ref(null)

  // ui data
  const cellType = ref('')
  const contextList = ref([])
  const homeData = ref({})
  const limit = ref(0)
  const isEndOfList = ref(false)
  const isError = ref(false)
  const isLoading = ref(true)
  const offset = ref(0)
  const subType = ref('')
  const title = ref('')
  const titleList = ref([])
  const topButtonList = ref([])
  const thumbnailInfo = ref(null)
  const code = ref('')

  const isApiTypeBand = computed(() => apiType.value === 'band')
  const isEditorPick = computed(() => subType.value === 'editor_pick_list')
  const hasCellList = computed(() => contextList.value?.length > 0)
  const catalogList = computed(() =>
    cellType.value === 'band_82' ? PreviewListCatalog : ListCatalog,
  )

  /**
   * view more title 세팅
   * @param layoutCatalog
   */
  const getListTitle = (layoutCatalog) => {
    const [titleListTitle] = titleList.value
    const catalogList = layoutCatalog?.catalogList ?? []
    const [catalog] = catalogList

    title.value = getTitle(catalog?.elementList) || titleListTitle?.text
  }

  /**
   * side filter 변경 이벤트
   * @param key
   * @param value
   */
  const onChangeSideFilter = ({ key, value }) => {
    if (!key) {
      return undefined
    }

    setQuery({
      ...queryRef.value,
      offset: 0,
      limit: limit.value,
      [key]: value,
    })

    appendQuery.value = {
      ...appendQuery.value,
      [key]: value,
    }

    pushRouter({
      name: PATH_NAME_LIST_VIEW_MORE,
      query: {
        ...originQuery.value,
        ...appendQuery.value,
      },
    })
  }

  /**
   * side filter 세팅
   * @param filter
   */
  const setFilterValues = (filter) => {
    filterListRef.value = filter?.filterList || []
    appendQuery.value = getAppendQuery(originQuery.value)

    queryRef.value = {
      ...queryRef.value,
      ...appendQuery.value,
    }
  }

  /**
   * filter 초기화
   * 필터 변경 후 뒤로가기 시 초기화된 offset 데이터로 filter 를 초기화
   */
  const resetFilter = () => {
    queryRef.value = setOriginQuery()
    setFilterValues(homeData.value?.layout?.filter)
    setQuery(queryRef.value)
  }

  /**
   * context (cell) list 업데이트 - 초기화 후 데이터 세팅
   * @returns {Promise<void>}
   */
  const updateContextList = async () => {
    scrollTop()
    clearData()
    resetFilter()

    await setData()
  }

  /**
   * context (cell) list 초기화
   * offset 초기화
   */
  const clearData = () => {
    contextList.value = []
    filterListRef.value = []
    offset.value = 0
  }

  /**
   * data fetch 이후 UI 세팅
   * @param data
   */
  const processListData = (data) => {
    if (!data) {
      return undefined
    }

    subType.value = data?.sub_type || ''
    titleList.value = data?.cell_toplist?.title_list || []
    thumbnailInfo.value = getThumbnailInfo(data?.cell_toplist?.cell_type || '')
    topButtonList.value = data?.cell_toplist?.topbuttonlist || []
    contextList.value = [...contextList.value, ...data?.cell_toplist?.celllist]
    cellType.value = data?.cell_toplist?.cell_type ?? []
    isEndOfList.value =
      data?.cell_toplist?.celllist?.length === 0 &&
      Number(data?.cell_toplist?.pagecount) < 1
  }

  /**
   * api 호출 url 을 home Api 에서 가져와
   * origin query, api query, offset, limit 세팅
   * @returns {{offset: UnwrapRef<number>, limit: UnwrapRef<number>}|undefined}
   */
  const setOriginQuery = () => {
    originUrl.value = route.path
    originQuery.value = route.query

    const url = getFetchUrl(homeData.value)
    if (!url) {
      return undefined
    }

    apiUrl.value = url?.split('?')?.[0]
    apiQuery.value = queryStringToObject(url)
    offset.value = Number(apiQuery.value?.offset) ?? 0
    limit.value = Number(apiQuery.value?.limit) ?? 20

    return {
      ...apiQuery.value,
      ...originQuery.value,
      offset: offset.value,
      limit: limit.value,
    }
  }

  /**
   * queryRef 값과 fetchUrl 값 세팅
   * @param query
   */
  const setQuery = (query) => {
    if (!query) {
      return
    }

    queryRef.value = {
      ...queryRef.value,
      ...query,
    }

    fetchUrl.value = `${apiUrl.value}?${stringify(queryRef.value)}`
  }

  /**
   * main, sub 외의 filter 관련 파라미터가 없다면 filter 의 초기 값으로 라우터 갱신 - 쿼리 워치 후 갱신에 사용
   */
  const updateQuery = () => {
    const originSideFilter = getSideFilterQuery(originQuery.value)

    if (isEmptyObject(originSideFilter)) {
      replaceRouter({
        ...originQuery.value,
        ...appendQuery.value,
      })
    }
  }

  /**
   * fetch data 를 통해
   * contextList (cell_list) 를 세팅
   * @returns {Promise<void>}
   */
  const setData = async () => {
    isLoading.value = true

    const data = await fetchData({
      fetchUrl: fetchUrl.value,
      homeData: homeData.value,
    })

    if (!data) {
      isError.value = true
      return undefined
    }

    processListData(data)
    isLoading.value = false
  }

  /**
   * load more
   * @returns {undefined}
   */
  const loadMore = () => {
    if (isEndOfList.value) {
      return undefined
    }

    offset.value += limit.value
    setQuery({
      ...apiQuery.value,
      ...appendQuery.value,
      offset: offset.value,
      limit: limit.value,
    })
    setData()
  }

  /**
   * filter 변경 초기화 시퀀스 (홈 api 호출하지 않는 초기화)
   */
  const filterChangedInit = () => {
    setQuery({
      ...queryRef.value,
      ...route.query,
    })
    updateContextList()
  }

  /**
   * init (홈 api 호출 하는 초기화)
   * @param uiCode
   * @returns {Promise<void>}
   */
  const initialize = async (uiCode) => {
    if (!uiCode) {
      isError.value = true
      return undefined
    }
    const { data } = await getViewMoreHomeData({ uiCode })

    if (!data) {
      isError.value = true
      return undefined
    }

    const { catalog, category, filter } = data?.layout
    homeData.value = data
    store.commit('setCategory', { category })

    setApiType(catalog)
    queryRef.value = setOriginQuery()
    setFilterValues(filter)
    setQuery(queryRef.value)
    updateQuery()
    await setData()
    getListTitle(catalog)
    scrollTop()
    titleTagNameChange(title.value)
  }

  const init = () => {
    const { code: uiCode } = router.currentRoute.query
    code.value = uiCode

    clearData()
    initialize(uiCode)
  }

  init()

  watch(
    () => route.query,
    (newQuery, oldQuery) => {
      const isFilterChanged =
        Object.keys(oldQuery).length === Object.keys(newQuery).length

      if (isFilterChanged) {
        filterChangedInit()
      }
    },
  )
</script>

<style scoped lang="scss">
  .list-view-more {
    min-height: 100vh;
  }
  .inner {
    max-width: 1240px;
    margin: 0 auto;
  }
  .mt-0 {
    margin-top: 0;
  }

  .list-view-more {
    .side-filter {
      padding: 20px 10px;

      &:last-child {
        margin-right: -10px;
      }

      .custom-select {
        margin-left: auto;
      }
    }

    .filter-wrapper {
      display: flex;
      justify-content: flex-end;
    }

    .contents-section {
      position: relative;
      overflow: hidden;
    }
  }

  .title {
    font-size: 28px;
    font-weight: 500;
    line-height: 40px;
  }

  .context-list {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 10px;
  }
</style>
